import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import BrokerageCalculator from "./BrokerageCalculator";
import "./FixedBrokerage.css";

// Internal route ("/open-account") renders <Link>; full URL renders <a>.
const OPEN_ACCOUNT = "/open-account";

const PLATFORMS = [
  {
    name: "Symphony XTS",
    kind: "API, OMS and algo framework",
    best: "quant and fully automated traders who code their own strategies.",
    points: [
      "REST APIs to build, test, deploy and monitor algos with live data and DMA.",
      "Full order and execution management.",
      "Order slicing beyond freeze limits.",
      "Time-based multi-leg options module.",
      "Connects to Excel, TradeStation and AmiBroker.",
    ],
  },
  {
    name: "GreekSoft",
    kind: "Options and arbitrage suite",
    best: "volatility, spread and arbitrage traders who price in IV and Greeks.",
    points: [
      "IV- and Vega-based spread order entry.",
      "One-click box, butterfly, calendar, conversion and reversion.",
      "Dedicated arbitrage window and pair trading.",
      "Option chain, option calculator and strategy finder.",
      "Pre-trade and position risk limits built in.",
    ],
  },
  {
    name: "Multitrade",
    kind: "Professional dealing terminal",
    best: "discretionary and semi-algo F&O traders placing hundreds of orders a day from one screen.",
    points: [
      "NSE-empanelled front end, built for exchange-member dealing desks.",
      "Keyboard-driven order entry and modification.",
      "Multi-leg and spread entry across futures and options.",
      "Live positions, MTM and margin utilisation in one view.",
      "Market watch and depth for many strikes at once.",
    ],
  },
];

const CHECKS = [
  {
    icon: "fa-right-left",
    title: "Your setup moves with you",
    body: "Bring your strategies to Symphony XTS, GreekSoft or Multitrade. Run both brokers in parallel until satisfied, then move capital.",
  },
  {
    icon: "fa-gauge-high",
    title: "Execution you can check",
    body: "Ask for our order-to-exchange latency logs before you move a rupee. Colocation available on NSE and BSE.",
  },
  {
    icon: "fa-shield-halved",
    title: "A broker since 1994",
    body: "SEBI-registered stock broker, member of NSE and BSE. Client funds are upstreamed to the clearing corporation as SEBI requires.",
  },
];

function useCtaHref(placement) {
  const { search } = useLocation();
  return useMemo(() => {
    const p = new URLSearchParams(search);
    if (!p.has("utm_source")) p.set("utm_source", "linkedin");
    if (!p.has("utm_medium")) p.set("utm_medium", "paid_social");
    if (!p.has("utm_campaign")) p.set("utm_campaign", "fixed_fo_brokerage");
    p.set("cta", placement);
    return `${OPEN_ACCOUNT}?${p.toString()}`;
  }, [search, placement]);
}

function Cta({ placement, children, className = "fb-btn fb-btn-primary" }) {
  const href = useCtaHref(placement);
  return href.startsWith("/") ? (
    <Link to={href} className={className}>{children}</Link>
  ) : (
    <a href={href} className={className} target="_blank" rel="noopener noreferrer">{children}</a>
  );
}

export default function FixedBrokerage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Fixed F&O brokerage: ₹1,000 per ₹10 lakh | Asiatic Stock & Securities";
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content",
      "Fixed monthly brokerage for professional F&O traders. ₹1,000 per ₹10 lakh of capital plus GST. Unlimited orders. Symphony XTS, GreekSoft, Multitrade.");
    return () => {
      document.title = prevTitle;
      if (prevDesc != null) meta.setAttribute("content", prevDesc);
    };
  }, []);

  const scrollToPlatforms = (e) => {
    e.preventDefault();
    document.getElementById("fb-platforms")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main className="fb">
      {/* <div className="fb-reg">
        <div className="container d-flex flex-wrap gap-3 gap-md-4">
          <span>SEBI Reg. INZ000166538</span>
          <span>NSE Member 07845</span>
          <span>BSE Member 6904</span>
          <span>Broker since 1994</span>
        </div>
      </div> */}

      <section className="fb-hero">
        <div className="container">
          <div className="row align-items-start">
            <div className="col-md-5">
              <p className="fb-tag">Fixed brokerage plan for professional traders</p>
              <h1 className="fb-h1">What did brokerage cost you last month?</h1>
              <p className="fb-lede">
                Per-order and turnover pricing grows with every trade you place. Ours grows only with
                the capital you deploy. Unlimited F&amp;O orders on NSE and BSE.
              </p>
              <div className="fb-price">
                <div className="fb-price-amt">₹1,000*</div>
                <div className="fb-price-per">
                  per ₹10 lakh of capital, per month. Unlimited orders.
                  <br />*Plus GST.
                </div>
              </div>
              <div className="d-flex flex-wrap gap-3">
                <Cta placement="hero">Open your account</Cta>
                <a href="#fb-platforms" onClick={scrollToPlatforms} className="fb-btn fb-btn-sage">
                  Compare platforms
                </a>
              </div>
              <dl className="fb-facts">
                <div><dt>0</dt><dd>per-order charges</dd></div>
                <div><dt>3</dt><dd>pro platforms</dd></div>
                <div><dt>30+</dt><dd>years in markets</dd></div>
              </dl>
            </div>
            <div className="col-md-7">
              <BrokerageCalculator />
            </div>
          </div>
        </div>
      </section> 

      <section id="fb-platforms" className="fb-section" aria-labelledby="fb-tools-h">
        <div className="container">
          <h2 id="fb-tools-h" className="fb-h2">Pick your <em>platform</em></h2>
          <p className="fb-intro">
            Same fixed brokerage on all three. Choose by workflow: fully coded algos, options structures, or manual speed.
          </p>
          <div className="row g-4">
            {PLATFORMS.map((p) => (
              <div className="col-lg-4" key={p.name}>
                <article className="fb-tool h-100">
                  <h3>{p.name}</h3>
                  <p className="fb-tool-kind">{p.kind}</p>
                  <p className="fb-tool-best"><b>Best for:</b> {p.best}</p>
                  <ul className="fb-list">
                    {p.points.map((pt) => (
                      <li key={pt}>
                        <i className="fa-solid fa-circle-check" aria-hidden="true" />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              </div>
            ))}
          </div>
          <p className="fb-fine">
            Automated strategies run under SEBI's algo framework and need exchange approval before going live.
            NSE and BSE colocation available for latency-sensitive strategies, subject to exchange rules and rack
            availability. Platform charges on request.
          </p>
        </div>
      </section>

      <section className="fb-section fb-tint" aria-labelledby="fb-why-h">
        <div className="container">
          <h2 id="fb-why-h" className="fb-h2">Before you <em>switch</em></h2>
          <p className="fb-intro">Three checks a professional trader runs before moving capital. Run them on us.</p>
          <div className="row g-4">
            {CHECKS.map((c) => (
              <div className="col-md-4" key={c.title}>
                <div className="fb-check h-100">
                  <i className={`fa-solid ${c.icon} fb-check-icon`} aria-hidden="true" />
                  <h3>{c.title}</h3>
                  <p>{c.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="fb-section text-center">
        <div className="container">
          <div className="fb-close">
            <h2 className="fb-h2">Stop paying <em>per order</em></h2>
            <p>₹1,000 per ₹10 lakh a month, plus GST. Unlimited orders included.</p>
            <Cta placement="closing">Open your account</Cta>
          </div>
        </div>
      </section>
    </main>
  );
}
